import { useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, Store } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/common";
import { PasswordInput } from "@/components/PasswordInput";
import { AddressForm } from "@/components/boy/AddressForm";
import { MapCheck } from "@/components/boy/MapCheck";
import { BoyGate, useBoyInfo } from "@/components/boy/BoyShell";
import { api, digits, errMsg, maskCep, maskDoc, maskPhone } from "@/lib/api";
import { validDoc } from "@/lib/document";
import { addrMissing, boyToken, EMPTY_ADDR } from "@/lib/boy";

const TONE = { error: "text-red-500", ok: "text-emerald-500", checking: "text-muted-foreground" };

function useDocCheck(code, setF) {
  const [st, setSt] = useState({});
  const last = useRef("");
  const check = async (raw) => {
    const d = digits(raw);
    if (d === last.current) return;
    last.current = d;
    if (!d) return setSt({});
    if (!validDoc(d)) return setSt({ status: "error", msg: d.length === 14 ? "CNPJ inválido" : d.length === 11 ? "CPF inválido" : "Informe um CPF (11 dígitos) ou CNPJ (14 dígitos)" });
    const { data } = await api.get(`/public/boy/${code}/document-check/${d}`).catch(() => ({ data: {} }));
    if (data.exists) return setSt({ status: "error", msg: "Já existe cadastro com este CPF/CNPJ. Entre com sua senha." });
    if (d.length === 11) return setSt({ status: "ok", msg: "CPF válido" });
    setSt({ status: "checking", msg: "Buscando dados da empresa na Receita…" });
    try {
      const { data: c } = await api.get(`/auth/cnpj/${d}`);
      const e = c.endereco;
      setF((x) => ({ ...x, name: x.name || c.nome_fantasia || c.razao_social, pin: null,
        address: { ...x.address, cep: maskCep(e.cep), street: e.logradouro, number: e.numero, complement: e.complemento, district: e.bairro, city: e.cidade, state: e.uf } }));
      setSt({ status: "ok", msg: `CNPJ válido · ${c.razao_social || "empresa"} · endereço preenchido` });
    } catch {
      setSt({ status: "ok", msg: "CNPJ válido · preencha o endereço" });
    }
  };
  return [st, check];
}

const missing = (f, st) => (f.name.trim().length < 2 ? "Informe o nome da empresa" : !validDoc(digits(f.document)) ? "CPF ou CNPJ inválido" : st.status === "error" ? st.msg
  : digits(f.phone).length < 10 ? "Informe o telefone com DDD" : digits(f.whatsapp).length < 10 ? "Informe o WhatsApp com DDD"
    : addrMissing(f.address, "empresa") || (f.password.length < 6 ? "A senha precisa ter pelo menos 6 caracteres" : f.password !== f.confirm ? "As senhas não conferem" : ""));

const PIN_KEYS = ["cep", "street", "district", "city", "state"];

export default function BoyMerchantSignup() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [info, err] = useBoyInfo(code);
  const [f, setF] = useState({ name: "", document: "", phone: "", whatsapp: "", address: EMPTY_ADDR, password: "", confirm: "", pin: null });
  const [busy, setBusy] = useState(false);
  const [st, checkDoc] = useDocCheck(code, setF);
  const set = (k, mask) => (e) => setF((x) => ({ ...x, [k]: mask ? mask(e.target.value) : e.target.value }));
  const onDoc = (e) => {
    const v = maskDoc(e.target.value);
    setF((x) => ({ ...x, document: v }));
    if (digits(v).length === 14) checkDoc(v);
  };
  const onAddr = (address) => setF((x) => ({ ...x, address, pin: PIN_KEYS.some((k) => address[k] !== x.address[k]) ? null : x.pin }));
  const submit = async (e) => {
    e.preventDefault();
    const m = missing(f, st);
    if (m) return toast.error(m);
    setBusy(true);
    try {
      const { pin, confirm, ...rest } = f;
      const { data } = await api.post(`/public/boy/${code}/merchants`, { ...rest, document: digits(f.document), phone: digits(f.phone), whatsapp: digits(f.whatsapp),
        address: { ...f.address, cep: digits(f.address.cep) }, point: pin && { lat: pin[0], lng: pin[1] } });
      boyToken.set(code, data.token);
      toast.success("Cadastro feito! Você já pode pedir entregas.");
      navigate(`/boy/${code}`);
    } catch (x) {
      toast.error(errMsg(x));
    } finally {
      setBusy(false);
    }
  };
  return (
    <BoyGate info={info} err={err} wide>
      <form onSubmit={submit} className="card-surface space-y-5 p-6" data-testid="boy-merchant-form">
        <div><h1 className="text-2xl font-bold">Cadastro de lojista</h1><p className="text-sm text-muted-foreground">Cadastre sua empresa para pedir entregas com os motoboys da {info?.store}.</p></div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="CPF ou CNPJ *" hint={st.msg && <span className={TONE[st.status]} data-testid="boy-merchant-doc-status">{st.msg}</span>}>
            <Input value={f.document} onChange={onDoc} onBlur={() => checkDoc(f.document)} inputMode="numeric" className={st.status === "error" ? "border-red-500" : ""} data-testid="boy-merchant-doc-input" />
          </Field>
          <Field label="Nome da empresa *"><Input value={f.name} onChange={set("name")} data-testid="boy-merchant-name-input" /></Field>
          <Field label="Telefone *"><Input value={f.phone} onChange={set("phone", maskPhone)} inputMode="tel" data-testid="boy-merchant-phone-input" /></Field>
          <Field label="WhatsApp *" hint="Recebe os avisos das corridas"><Input value={f.whatsapp} onChange={set("whatsapp", maskPhone)} inputMode="tel" data-testid="boy-merchant-whatsapp-input" /></Field>
        </div>
        <section className="space-y-3">
          <p className="eyebrow">Endereço</p>
          <AddressForm value={f.address} onChange={onAddr} id="boy-merchant-addr" />
          <MapCheck key={PIN_KEYS.map((k) => f.address[k]).join("|")} code={code} addr={f.address} value={f.pin} onSave={(pin) => setF((x) => ({ ...x, pin }))} />
        </section>
        <section className="grid gap-3 sm:grid-cols-2">
          <p className="eyebrow sm:col-span-2">Senha de acesso</p>
          <Field label="Senha *" hint="Mínimo de 6 caracteres"><PasswordInput value={f.password} onChange={set("password")} autoComplete="new-password" testId="boy-merchant-password-input" /></Field>
          <Field label="Confirmar senha *"><PasswordInput value={f.confirm} onChange={set("confirm")} autoComplete="new-password" testId="boy-merchant-confirm-input" /></Field>
        </section>
        <Button type="submit" className="h-12 w-full text-base" disabled={busy} data-testid="boy-merchant-submit">
          {busy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Store className="mr-2 h-5 w-5" />}Cadastrar e começar
        </Button>
        <p className="text-center text-sm text-muted-foreground">Já tem cadastro? <Link to={`/boy/${code}`} className="font-medium text-primary hover:underline" data-testid="boy-merchant-login-link">Entrar</Link></p>
      </form>
    </BoyGate>
  );
}
